import React, { useRef, useEffect } from 'react';
import './ThumbnailBar.css';

const ThumbnailBar = ({ images, selectedImage, onImageSelect }) => {
  const scrollContainerRef = useRef(null);
  const selectedThumbRef = useRef(null);
  const shouldScrollRef = useRef(true); // Track if we should scroll
  
  // Detect if device is desktop (not mobile/tablet) - use a function to check current width
  const isDesktop = () => window.innerWidth > 768;

  useEffect(() => {
    // Only scroll when explicitly requested (on click, not hover)
    if (selectedImage && scrollContainerRef.current && shouldScrollRef.current) {
      console.log('Selected image changed:', selectedImage.path);
      
      // Use requestAnimationFrame to ensure DOM is fully updated
      const scrollToSelected = () => {
        const container = scrollContainerRef.current;
        if (!container) return;
        
        // Find the selected thumbnail by class instead of ref
        const selectedThumb = container.querySelector('.thumbnail.selected');
        if (selectedThumb) {
          // Get the thumbnail wrapper which is the actual positioned element
          const thumbWrapper = selectedThumb.parentElement;
          const containerWidth = container.offsetWidth;
          const thumbLeft = thumbWrapper.offsetLeft;
          const thumbWidth = selectedThumb.offsetWidth;
          
          console.log('Scrolling to center thumbnail:', { 
            thumbLeft, 
            containerWidth,
            scrollWidth: container.scrollWidth,
            currentScroll: container.scrollLeft
          });
          
          // Center the selected thumbnail in the container
          const scrollLeft = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);
          console.log('Target scroll position:', scrollLeft);
          container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
        } else {
          console.log('Selected thumbnail not found in DOM');
        }
      };
      
      // Use double requestAnimationFrame to ensure React has finished rendering
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollToSelected);
      });
    }
    
    // Reset the scroll flag after processing
    shouldScrollRef.current = true;
  }, [selectedImage]);

  // Group images by month and year for labels (considering reversed order)
  const monthYearMap = new Map();
  const reversedImages = images.slice().reverse();
  reversedImages.forEach((image, index) => {
    const date = new Date(image.date);
    const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const year = date.getFullYear();
    
    if (!monthYearMap.has(monthYear)) {
      monthYearMap.set(monthYear, {
        firstIndex: index,
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        year: year,
        showYear: false
      });
    }
  });

  // Mark where to show year labels
  let lastYear = null;
  monthYearMap.forEach((value) => {
    if (value.year !== lastYear) {
      value.showYear = true;
      lastYear = value.year;
    }
  });

  // Format selected date
  const formatSelectedDate = () => {
    if (!selectedImage) return '';
    const date = new Date(selectedImage.date);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Handle hover for desktop devices (no scrolling)
  const handleThumbnailHover = (image) => {
    console.log('Thumbnail hover triggered for:', image.date, 'isDesktop:', isDesktop(), 'windowWidth:', window.innerWidth);
    if (isDesktop()) {
      shouldScrollRef.current = false; // Disable scrolling for hover
      onImageSelect(image);
    }
  };

  // Handle click (with scrolling)
  const handleThumbnailClick = (image) => {
    console.log('Thumbnail click triggered for:', image.date);
    shouldScrollRef.current = true; // Enable scrolling for click
    onImageSelect(image);
  };

  return (
    <div className="thumbnail-bar-container">
      <div className="selected-date">
        {formatSelectedDate()}
      </div>
      <div className="thumbnail-scroll-container" ref={scrollContainerRef}>
        <div className="thumbnail-track">
          {images.slice().reverse().map((image, index) => {
            const isSelected = selectedImage && selectedImage.path === image.path;
            const date = new Date(image.date);
            const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const monthData = monthYearMap.get(monthYear);
            const showMonthLabel = monthData && monthData.firstIndex === index;
            
            return (
              <div key={image.path} className="thumbnail-wrapper">
                <div
                  ref={isSelected ? selectedThumbRef : null}
                  className={`thumbnail ${isSelected ? 'selected' : ''} ${
                    image.superresolved ? 'superresolved' : ''
                  }`}
                  onClick={() => handleThumbnailClick(image)}
                  onMouseEnter={() => handleThumbnailHover(image)}
                  onMouseLeave={() => console.log('Mouse left thumbnail:', image.date)}
                  title={new Date(image.date).toLocaleDateString()}
                >
                  {image.url ? (
                    <img src={image.url} alt={`Thumbnail ${index}`} />
                  ) : (
                    <div className="thumbnail-placeholder"></div>
                  )}
                </div>
                {showMonthLabel && index > 0 && (
                  <div 
                    className="month-separator" 
                    style={{ left: '-1px' }}
                  />
                )}
                {showMonthLabel && (
                  <div className="time-labels">
                    <div className="month-label">{monthData.month}</div>
                    {monthData.showYear && (
                      <div className="year-label">{monthData.year}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ThumbnailBar;